/**
 * Spinner — branded loading indicator with rotating contextual messages.
 *
 * Type semantics:
 *   thinking  → primary brand color, rotates contemplative messages
 *   tool      → secondary (violet) — local tool execution
 *   mcp       → warning (amber) — MCP / external server activity
 *   receiving → success (emerald) — tokens streaming in
 *
 * Edge cases handled:
 *   • Reduced-motion / non-TTY environments still render a static label.
 *   • `charCount` of 0 or negative is suppressed (avoids "(0 chars)").
 *   • Falls back to the supplied `label` if no THINKING_MESSAGES exist.
 */

import React, { useState, useEffect } from 'react';
import { Box, Text } from 'ink';
import InkSpinner from 'ink-spinner';
import { COLORS, THINKING_MESSAGES, SYMBOLS } from '../theme/ink-theme.js';

interface SpinnerProps {
    label?: string;
    type?: 'thinking' | 'tool' | 'mcp' | 'receiving';
    charCount?: number;
}

const ROTATION_INTERVAL_MS = 3000;

export const Spinner: React.FC<SpinnerProps> = ({
    label = 'Thinking…',
    type = 'thinking',
    charCount,
}) => {
    const [messageIndex, setMessageIndex] = useState(0);

    useEffect(() => {
        if (type !== 'thinking' || THINKING_MESSAGES.length <= 1) return undefined;
        const interval = setInterval(() => {
            setMessageIndex(prev => (prev + 1) % THINKING_MESSAGES.length);
        }, ROTATION_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [type]);

    const color =
        type === 'mcp' ? COLORS.warning
        : type === 'tool' ? COLORS.secondary
        : type === 'receiving' ? COLORS.success
        : COLORS.primary;

    const prefix =
        type === 'mcp' ? SYMBOLS.mcp
        : type === 'tool' ? SYMBOLS.tool
        : type === 'receiving' ? SYMBOLS.sparklet
        : SYMBOLS.arch;

    const displayLabel = (() => {
        if (type === 'thinking') {
            return THINKING_MESSAGES[messageIndex] ?? label;
        }
        if (type === 'receiving' && typeof charCount === 'number' && charCount > 0) {
            return `Receiving… (${charCount.toLocaleString()} chars)`;
        }
        return label;
    })();

    return (
        <Box>
            <Text color={color}><InkSpinner type="dots" /></Text>
            <Text color={color}> {prefix}</Text>
            <Text> </Text>
            <Text dimColor>{displayLabel}</Text>
        </Box>
    );
};
